import { Box, CircularProgress } from '@mui/material'
import { Suspense, lazy } from 'react'
import { Navigate, createBrowserRouter, useParams } from 'react-router-dom'

import { useMe } from '@/api/session'
import { AuthGuard } from '@/components/AuthGuard'
import { Shell } from '@/components/Shell'
import { Landing } from '@/pages/Landing'
import { NotFound } from '@/pages/NotFound'
// Eager. These three are the whole reason the product works: somebody who has
// never heard of us, on a phone, on a bad network, arriving from an email.
// They must not wait on a second round trip.
import { Invite } from '@/pages/Invite'
import { Pay } from '@/pages/Pay'
// Reached from a group chat, on a phone, courtside. Same audience as the
// two above, so it waits on nothing either.
import { Score } from '@/pages/Score'
import { SignIn } from '@/pages/SignIn'
import { Unsubscribe } from '@/pages/Unsubscribe'

// Lazy. Everything a manager touches — and with it the DataGrid, which is most
// of the bundle. A payer should never download the roster grid to read one
// number, so the split is drawn exactly where the audience changes.
const Archive = lazy(() => import('@/pages/Archive').then((m) => ({ default: m.Archive })))
const CampaignScreenRoute = lazy(() =>
  import('@/pages/CampaignScreen').then((m) => ({
    default: m.CampaignScreenRoute,
  })),
)
const Campaigns = lazy(() =>
  import('@/pages/Campaigns').then((m) => ({ default: m.Campaigns })),
)
const Games = lazy(() => import('@/pages/Games').then((m) => ({ default: m.Games })))
const History = lazy(() => import('@/pages/History').then((m) => ({ default: m.History })))
const Expenses = lazy(() => import('@/pages/Expenses').then((m) => ({ default: m.Expenses })))
const People = lazy(() => import('@/pages/People').then((m) => ({ default: m.People })))
const Person = lazy(() => import('@/pages/Person').then((m) => ({ default: m.Person })))
const Profile = lazy(() =>
  import('@/pages/Profile').then((m) => ({ default: m.Profile })),
)
const Admin = lazy(() => import('@/pages/Admin').then((m) => ({ default: m.Admin })))

export function LegacyLink({ to }: { to: 'pay' }) {
  const { token = '' } = useParams()
  // `replace`, so Back returns to the email rather than bouncing through here.
  return <Navigate to={`/${to}/${token}`} replace />
}

/**
 * `/` answers two different people.
 *
 * A signed-out visitor gets the public page that says what this is — Google's
 * OAuth review fetches this URL without a session, and the dashboard used to
 * bounce it to /signin, which failed verification as "home page behind a login
 * page". Somebody signed in gets their campaigns, which is what `/` has always
 * meant to them, so no bookmark changes.
 */
export function Home() {
  const me = useMe()
  if (me.isPending) return <Loading />
  if (me.data?.signed_in) return <Navigate to="/campaigns" replace />
  return <Landing />
}

function Loading() {
  return (
    <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
      <CircularProgress size={22} />
    </Box>
  )
}

/**
 * Two groups of routes, and the split is the point.
 *
 * Everything under the guard is somebody managing money they are owed.
 * Everything outside it — pay and unsubscribe — is a stranger arriving from
 * an email, and putting any of those behind the guard would break the only
 * path that genuinely has to work.
 */
/**
 * The route table, exported separately from the router built on it.
 *
 * A `createBrowserRouter` reaches for window.history when it is constructed,
 * so its `.routes` cannot simply be handed to a memory router in a test —
 * the routes are there but nothing mounts. Keeping the table as plain data
 * means tests exercise the real one rather than a copy that can drift.
 */
export const routes = [
  // Public, and the reason is in `Home`.
  { path: '/', element: <Home /> },
  { path: '/signin', element: <SignIn /> },

  // The link shape the Django app mailed out.
  //
  // The API redirects these too, but the API is not what a person clicking a
  // link in an old email reaches — they land on whatever serves the web app,
  // and the router had no route for them, so a perfectly good token rendered
  // "Nothing here". A link in an email is forever; both origins have to
  // answer.
  { path: '/p/:token', element: <LegacyLink to="pay" /> },
  // `/j/` is gone with `join_token`: joining happens on the campaign screen,
  // and no join link was ever sent to a real person.

  // Public. No shell, no nav, no sign-in wall.
  { path: '/pay/:token', element: <Pay /> },
  // The invite, and eager for the same reason the pay page is: somebody on a
  // phone, arriving from a message, who has never heard of us. Answering must
  // not wait on a second round trip.
  { path: '/r/:token', element: <Invite /> },
  // The scoring link. Public, and its one power is typing a result: it
  // cannot add a player, rename anything or read the table. The standings
  // are behind sign-in at /c/:token/games with the roster they are about.
  { path: '/s/:token', element: <Score /> },
  { path: '/unsubscribe/:token', element: <Unsubscribe /> },

  // No routes: /sms, /privacy and /terms are served as real HTML by the API,
  // ahead of the SPA fallback. A carrier vetting tool does not run
  // JavaScript, so a React page there was an empty shell to the only reader
  // that mattered — which is how three A2P submissions were rejected for
  // language that was correct and present. /privacy stays a document for a
  // second reason: Google's OAuth reviewer fetches it without a session too.
  //
  // Anything linking to them must use a plain <a href>, not a RouterLink: a
  // client-side navigation would look for a route that is deliberately gone.

  {
    element: (
      <AuthGuard>
        <Suspense fallback={<Loading />}>
          <Shell />
        </Suspense>
      </AuthGuard>
    ),
    children: [
      { path: '/campaigns', element: <Campaigns /> },
      { path: '/archive', element: <Archive /> },
      // The same component as /c/:token. A new campaign is a campaign that
      // has not been saved yet, not a different kind of thing.
      { path: '/new', element: <CampaignScreenRoute /> },
      // Two doors onto the same screen, and the route pre-sets which shape
      // rather than asking. `docs/SHAPES.md` objected to a PICKER — a screen
      // that stops you with "choose a type" before you have typed anything —
      // and two labelled doors are not that: nobody opens this app unsure
      // whether they need money back or a headcount.
      { path: '/new/money', element: <CampaignScreenRoute /> },
      { path: '/new/rsvp', element: <CampaignScreenRoute /> },
      { path: '/new/group', element: <CampaignScreenRoute /> },
      { path: '/people', element: <People /> },
      { path: '/people/:personId', element: <Person /> },
      { path: '/c/:token', element: <CampaignScreenRoute /> },
      { path: '/c/:token/history', element: <History /> },
      // Behind the same AuthGuard as everything else here, which is
      // the whole of "with login": a receipt says where somebody
      // shops and when, so the token alone is not enough for it.
      { path: '/c/:token/expenses', element: <Expenses /> },
      { path: '/c/:token/games', element: <Games /> },
      { path: '/admin', element: <Admin /> },
      { path: '/profile', element: <Profile /> },
    ],
  },

  { path: '*', element: <NotFound /> },
]

export const router = createBrowserRouter(routes)
